"use client";

/**
 * SlideshowImage — auto-rotates through an array of image URLs with a
 * TRUE crossfade. Two layered <Image> elements: while the new one fades in,
 * the old one stays on screen and fades out at the same time, so the parent
 * background is never visible between slides. No more dip-to-black.
 *
 * Defaults match Gordon's spec:
 *   • interval  = 6000 ms between slide changes
 *   • fadeMs    = 3000 ms crossfade (the "fade 3 seconds between each images")
 *   The interval must be ≥ fadeMs; we clamp it so the new image has finished
 *   appearing before the next swap is scheduled.
 *
 * Skipped entirely on touch / small screens — iOS Safari OOMs after many
 * simultaneous full-res decodes across a long feed.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { useBranding } from "@/contexts/branding-context";
import { isVideoUrl } from "@/lib/media-kind";

/** Transition styles supported by the slideshow. Admin can pick the
 *  default in Super Admin → Themes → Slideshow; per-instance override
 *  via the `transition` prop. */
export type SlideshowTransition =
  | "crossfade"        // pure opacity (default — no dip to black)
  | "fade-black"       // both slides dip through pure black mid-transition
  | "fade-white"       // both slides dip through pure white
  | "slide-left"       // new slide enters from the right, old exits to the left
  | "slide-right"
  | "slide-up"
  | "slide-down"
  | "zoom-in"          // new slide grows in, old shrinks under it
  | "zoom-out"         // new slide grows from afar
  | "blur"             // both slides blur during crossfade
  | "ken-burns"        // slow pan + scale, crossfade between
  | "wipe-left"        // diagonal mask reveal
  | "flip"             // 3D Y-axis flip
  | "swirl";           // mild rotate + scale crossfade

export const TRANSITION_LABELS: Record<SlideshowTransition, string> = {
  "crossfade":   "Crossfade",
  "fade-black":  "Fade through black",
  "fade-white":  "Fade through white",
  "slide-left":  "Slide ← left",
  "slide-right": "Slide → right",
  "slide-up":    "Slide ↑ up",
  "slide-down":  "Slide ↓ down",
  "zoom-in":     "Zoom in",
  "zoom-out":    "Zoom out",
  "blur":        "Blur fade",
  "ken-burns":   "Ken Burns",
  "wipe-left":   "Wipe ← left",
  "flip":        "Flip",
  "swirl":       "Swirl",
};

interface SlideshowImageProps {
  /** Ordered list of image URLs. Falsy values are filtered out. */
  images: string[];
  alt: string;
  className?: string;
  /** ms between slide changes — default 6 000. Will be clamped to ≥ fadeMs. */
  interval?: number;
  /** ms for the crossfade itself — default 3 000. */
  fadeMs?: number;
  /** Transition style — default "crossfade". */
  transition?: SlideshowTransition;
  unoptimized?: boolean;
  priority?: boolean;
  /** The size it's shown at (next/image `sizes`). Without it a `fill` image is
   *  assumed full-screen-wide — a 56px thumbnail fetched ~1000px (PageSpeed). */
  sizes?: string;
  /** A single big artwork (player sheet, track page): autoplays on touch
   *  devices too, swipes left/right and has tappable dots (Gordon 2026-10-31:
   *  "autoplay images slide show not working and I can't swipe"). Off for
   *  feed cards — many rotating decodes at once is what OOMed iOS Safari. */
  interactive?: boolean;
  /** Shown when every image fails to load (a dead link) — e.g. the BB
   *  generated art — instead of a broken-image icon (Gordon 2026-10-31). */
  fallback?: React.ReactNode;
}

function renderSlide(
  src: string,
  alt: string,
  style: React.CSSProperties,
  unoptimized: boolean,
  priority: boolean,
  className?: string,
  sizes?: string,
  onError?: () => void,
) {
  if (isVideoUrl(src)) {
    return (
      <video
        key={src}
        src={src}
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
        className={cn("absolute inset-0 h-full w-full object-cover", className)}
        style={style}
        onError={onError}
      />
    );
  }
  return (
    <Image
      src={src}
      alt={alt}
      fill
      sizes={sizes}
      priority={priority}
      unoptimized={unoptimized}
      className={cn("object-cover", className)}
      style={style}
      onError={onError}
    />
  );
}

export function SlideshowImage(props: SlideshowImageProps) {
  // Read admin defaults from BrandingContext. Per-component props always win;
  // missing props fall through to siteSettings/appearance (slideshowIntervalSec
  // / slideshowFadeSec / slideshowTransition) then to the hard-coded defaults
  // matching Gordon's "fade 3 seconds between each images" spec.
  const branding = useBranding();
  const settings = (branding as any)?.settings || {};
  const interval = props.interval ?? Math.round((settings.slideshowIntervalSec ?? 6) * 1000);
  const fadeMs   = props.fadeMs   ?? Math.round((settings.slideshowFadeSec     ?? 3) * 1000);
  const transition = (props.transition ?? settings.slideshowTransition ?? "crossfade") as SlideshowTransition;
  const {
    images,
    alt,
    className,
    // Default OFF: images now go through the Next.js optimizer (resized WebP)
    // — serving full-res originals was blowing iOS Safari's memory ceiling.
    unoptimized = false,
    priority = false,
    sizes,
    interactive = false,
  } = props;
  void interval; void fadeMs; void transition; // silence "declared but only used" before the body uses them
  // Images that failed to load (dead links) drop out of the rotation.
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const markFailed = useCallback((src: string) => setFailed(prev => (prev.has(src) ? prev : new Set(prev).add(src))), []);
  const valid = images.filter(Boolean).filter(u => !failed.has(u));
  const multi = valid.length > 1;

  // Two layered slots. `topIsA` decides which slot is the new (incoming)
  // image. On each tick we set the OTHER slot's src to the next image and
  // flip the flag — CSS handles the crossfade via opacity transition.
  const [aSrc, setASrc] = useState<string>(valid[0] || "");
  const [bSrc, setBSrc] = useState<string>(valid[1 % Math.max(1, valid.length)] || valid[0] || "");
  const [topIsA, setTopIsA] = useState(true);
  const [paused, setPaused] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const idxRef = useRef(0); // tracks the index currently showing on top

  // Reset the layered slots when the `images` prop changes. useState only
  // initialises on mount, so a parent re-render with new images was leaving
  // aSrc / bSrc pointing at the PREVIOUS images — that's the "media player
  // footer is still showing the previous played track's thumbnail" bug
  // Gordon kept hitting. Identity-compare via the joined URL string so we
  // don't reset on every parent render that happens to recreate the array.
  const imagesKey = valid.join("|");
  useEffect(() => {
    setASrc(valid[0] || "");
    setBSrc(valid[1 % Math.max(1, valid.length)] || valid[0] || "");
    setTopIsA(true);
    idxRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesKey]);

  // Hide rotation on touch / mobile — too expensive in image decodes across a
  // feed. An `interactive` (single, big) slideshow rotates everywhere.
  const isMobile =
    !interactive &&
    typeof window !== "undefined" &&
    (window.matchMedia?.("(max-width: 767px)").matches ||
      window.matchMedia?.("(pointer: coarse)").matches);

  // A swipe or dot tap moves quickly; autoplay keeps the slow crossfade.
  const [quick, setQuick] = useState(false);
  const quickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const goTo = useCallback((target: number, manual = false) => {
    if (valid.length <= 1) return;
    const next = ((target % valid.length) + valid.length) % valid.length;
    if (next === idxRef.current) return;
    if (manual) {
      setQuick(true);
      if (quickTimer.current) clearTimeout(quickTimer.current);
      quickTimer.current = setTimeout(() => setQuick(false), 400);
    }
    idxRef.current = next;
    // Preload the next URL into the currently-hidden slot, THEN flip which
    // slot is on top so the new image is fully decoded before crossfading in.
    if (topIsA) {
      setBSrc(valid[next]);
      // Give the browser a frame to start decoding before flipping.
      requestAnimationFrame(() => setTopIsA(false));
    } else {
      setASrc(valid[next]);
      requestAnimationFrame(() => setTopIsA(true));
    }
  }, [valid, topIsA]);
  const advance = useCallback(() => goTo(idxRef.current + 1), [goTo]);
  const touchX = useRef<number | null>(null);

  // Track on-screen visibility — off-screen cards don't tick.
  useEffect(() => {
    if (!multi || isMobile) return;
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setOnScreen(true);
      return;
    }
    const obs = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { rootMargin: "100px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [multi, isMobile]);

  useEffect(() => {
    if (!multi || paused || !onScreen || isMobile) return;
    // Clamp the interval so the fade always finishes BEFORE the next swap.
    const safeInterval = Math.max(interval, fadeMs + 200);
    const id = setInterval(advance, safeInterval);
    return () => clearInterval(id);
  }, [multi, paused, onScreen, isMobile, advance, interval, fadeMs]);

  if (valid.length === 0) return <>{props.fallback ?? null}</>;

  /** Style packs for each slot, parameterised by whether it's the visible
   *  (incoming) slot or the outgoing one. All times use `fadeMs`. */
  const slotStyle = (isVisible: boolean): React.CSSProperties => {
    const t = `${quick ? 350 : fadeMs}ms`;
    const ease = "ease-in-out";
    const base: React.CSSProperties = {
      transition: `opacity ${t} ${ease}, transform ${t} ${ease}, filter ${t} ${ease}, clip-path ${t} ${ease}`,
      willChange: "opacity, transform, filter",
      transformOrigin: "center",
    };
    switch (transition) {
      case "crossfade":
        return { ...base, opacity: isVisible ? 1 : 0 };
      case "fade-black":
      case "fade-white":
        return { ...base, opacity: isVisible ? 1 : 0 };
      case "slide-left":
        return { ...base, opacity: isVisible ? 1 : 0, transform: isVisible ? "translateX(0)" : "translateX(-30%)" };
      case "slide-right":
        return { ...base, opacity: isVisible ? 1 : 0, transform: isVisible ? "translateX(0)" : "translateX(30%)" };
      case "slide-up":
        return { ...base, opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(-30%)" };
      case "slide-down":
        return { ...base, opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(30%)" };
      case "zoom-in":
        return { ...base, opacity: isVisible ? 1 : 0, transform: isVisible ? "scale(1)" : "scale(0.85)" };
      case "zoom-out":
        return { ...base, opacity: isVisible ? 1 : 0, transform: isVisible ? "scale(1)" : "scale(1.18)" };
      case "blur":
        return { ...base, opacity: isVisible ? 1 : 0, filter: isVisible ? "blur(0)" : "blur(18px)" };
      case "ken-burns":
        return {
          ...base,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1.04) translate(0.5%, -0.5%)" : "scale(1) translate(0,0)",
        };
      case "wipe-left":
        return {
          ...base,
          opacity: 1,
          clipPath: isVisible ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
        };
      case "flip":
        return {
          ...base,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "rotateY(0deg)" : "rotateY(90deg)",
        };
      case "swirl":
        return {
          ...base,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "rotate(0deg) scale(1)" : "rotate(8deg) scale(1.12)",
        };
      default:
        return { ...base, opacity: isVisible ? 1 : 0 };
    }
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full overflow-hidden"
      style={{
        ...(transition === "flip" ? { perspective: "1000px" } : {}),
        // Interactive: horizontal swipes are ours; vertical ones still scroll.
        ...(interactive && multi ? { touchAction: "pan-y" as const } : {}),
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      {...(interactive && multi ? {
        onTouchStart: (e: React.TouchEvent) => { touchX.current = e.touches[0].clientX; setPaused(true); },
        onTouchEnd: (e: React.TouchEvent) => {
          const x0 = touchX.current; touchX.current = null; setPaused(false);
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 40) goTo(idxRef.current + (dx < 0 ? 1 : -1), true);
        },
      } : {})}
    >
      {/* Fade-through-colour transitions use a coloured layer between the
          two slots. Its opacity peaks at 1 at mid-transition. We drive the
          colour-layer with the SAME opacity keyframe via CSS — it sits at
          z-index 1 between the images. */}
      {(transition === "fade-black" || transition === "fade-white") && (
        <div
          aria-hidden
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            backgroundColor: transition === "fade-black" ? "#000" : "#fff",
            opacity: topIsA ? 0 : 0, // both stable states = transparent
            transition: `opacity ${fadeMs / 2}ms ease-in, opacity ${fadeMs / 2}ms ease-out`,
            // The mid-transition flash is created by keying off React state:
            // a CSS keyframe animation triggers when topIsA flips.
            animation: `bb-slide-flash ${fadeMs}ms ease-in-out`,
            animationPlayState: "paused",
          }}
        />
      )}

      {/* Slots — a slide can be a static image OR a short video/GIF clip
          (isVideoUrl), same crossfade styling either way. */}
      {renderSlide(aSrc || valid[0], alt, slotStyle(topIsA), unoptimized, priority, className, sizes, () => markFailed(aSrc || valid[0]))}
      {renderSlide(bSrc || valid[0], alt, slotStyle(!topIsA), unoptimized, false, className, sizes, () => markFailed(bSrc || valid[0]))}

      {/* Dot indicators */}
      {multi && !interactive && (
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-10 pointer-events-none">
          {valid.map((_, i) => (
            <span
              key={i}
              className={cn(
                "block w-1 h-1 rounded-full transition-all duration-200",
                i === idxRef.current ? "bg-white scale-125" : "bg-white/50",
              )}
            />
          ))}
        </div>
      )}
      {/* Interactive: bigger dots, each a tap target (padded to 24px). */}
      {multi && interactive && (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex z-10">
          {valid.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`${i + 1} / ${valid.length}`}
              aria-current={i === idxRef.current ? "true" : undefined}
              onClick={(e) => { e.stopPropagation(); goTo(i, true); }}
              className="grid h-6 w-4 place-items-center"
            >
              <span className={cn("block h-1.5 w-1.5 rounded-full transition-all duration-200", i === idxRef.current ? "bg-white scale-125" : "bg-white/50")} />
            </button>
          ))}
        </div>
      )}

      {/* Keyframe used by fade-through-colour transitions. */}
      <style>{`
        @keyframes bb-slide-flash {
          0%, 100% { opacity: 0; }
          50%      { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
